/**
 * DateRangePicker Component
 *
 * Select a date range with preset options and custom date selection.
 * Supports Dutch locale and follows the myVault design system.
 *
 * Features:
 * - Preset ranges (Last 7 days, Last 30 days, This month, etc.)
 * - Custom date range selection
 * - Locale support (default: nl-NL)
 * - Keyboard accessible
 */

import { forwardRef, useState, useRef, useEffect } from 'react';
import { CalendarIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

import { cn } from '../../utils/cn';
import {
  DATE_RANGE_PRESETS,
  formatDateRange,
  formatDateForInput,
  parseDateFromInput,
  type DateRange,
  type DateRangePreset,
} from './presets';

export interface DateRangePickerProps {
  /** Current selected date range */
  value?: DateRange | null;
  /** Called when the date range changes */
  onChange?: (range: DateRange | null) => void;
  /** Locale for date formatting (default: 'nl-NL') */
  locale?: string;
  /** Which presets to show (default: all) */
  presets?: string[];
  /** Placeholder text */
  placeholder?: string;
  /** Whether the input has an error state */
  error?: boolean;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Additional className */
  className?: string;
  /** Minimum selectable date */
  minDate?: Date;
  /** Maximum selectable date */
  maxDate?: Date;
}

export const DateRangePicker = forwardRef<HTMLDivElement, DateRangePickerProps>(
  (
    {
      value,
      onChange,
      locale = 'nl-NL',
      presets,
      placeholder,
      error,
      disabled,
      className,
      minDate,
      maxDate,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [customStart, setCustomStart] = useState<string>('');
    const [customEnd, setCustomEnd] = useState<string>('');
    const [activeTab, setActiveTab] = useState<'presets' | 'custom'>('presets');
    const containerRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Filter presets if specified
    const availablePresets = presets
      ? DATE_RANGE_PRESETS.filter((p) => presets.includes(p.id))
      : DATE_RANGE_PRESETS;

    // Get display text
    const displayText = value
      ? formatDateRange(value, locale)
      : placeholder || (locale === 'nl-NL' ? 'Selecteer periode' : 'Select period');

    // Handle preset selection
    const handlePresetClick = (preset: DateRangePreset) => {
      const range = preset.getRange();
      onChange?.(range);
      setIsOpen(false);
    };

    // Handle custom range apply
    const handleCustomApply = () => {
      const start = parseDateFromInput(customStart);
      const end = parseDateFromInput(customEnd);

      if (start && end) {
        // Validate range
        if (start > end) {
          return; // Invalid range
        }
        if (minDate && start < minDate) {
          return; // Before minimum
        }
        if (maxDate && end > maxDate) {
          return; // After maximum
        }

        onChange?.({ start, end });
        setIsOpen(false);
        setCustomStart('');
        setCustomEnd('');
      }
    };

    // Handle clear
    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange?.(null);
      setCustomStart('');
      setCustomEnd('');
    };

    // Close on click outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
      }
    }, [isOpen]);

    // Close on Escape
    useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
      }
    }, [isOpen]);

    // Merge forwarded ref with container ref
    const setRefs = (el: HTMLDivElement | null) => {
      // Set containerRef
      (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
      // Set forwarded ref
      if (typeof ref === 'function') {
        ref(el);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
      }
    };

    return (
      <div ref={setRefs} className={cn('relative', className)}>
        {/* Trigger button */}
        <button
          ref={buttonRef}
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={cn(
            'flex h-10 w-full items-center justify-between gap-2 rounded border bg-white px-3 py-2 text-sm',
            'focus:border-mv-blue-500 focus:outline-none focus:ring-2 focus:ring-mv-blue-500/20',
            'disabled:cursor-not-allowed disabled:bg-mv-gray-50 disabled:opacity-50',
            error
              ? 'border-mv-red-600 focus:border-mv-red-600 focus:ring-mv-red-600/20'
              : 'border-mv-gray-300',
            // GAP-015: text-mv-gray-500 for WCAG AA contrast
            value ? 'text-mv-gray-900' : 'text-mv-gray-500'
          )}
          aria-haspopup="true"
          aria-expanded={isOpen}
        >
          <div className="flex items-center gap-2 truncate">
            <CalendarIcon className="h-4 w-4 flex-shrink-0 text-mv-gray-500" aria-hidden="true" />
            <span className="truncate">{displayText}</span>
          </div>
          <div className="flex items-center gap-1">
            {value && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                // GAP-015: text-mv-gray-500 for WCAG AA contrast
                className="rounded p-0.5 text-mv-gray-500 hover:bg-mv-gray-100 hover:text-mv-gray-700"
                aria-label={locale === 'nl-NL' ? 'Wissen' : 'Clear'}
              >
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
            <ChevronDownIcon
              className={cn(
                'h-4 w-4 flex-shrink-0 text-mv-gray-500 transition-transform',
                isOpen && 'rotate-180'
              )}
              aria-hidden="true"
            />
          </div>
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div
            className={cn(
              'absolute z-50 mt-1 w-full min-w-[320px] rounded-lg border border-mv-gray-200 bg-white shadow-lg',
              'animate-in fade-in slide-in-from-top-2 duration-200'
            )}
          >
            {/* Tabs */}
            <div className="flex border-b border-mv-gray-200">
              <button
                type="button"
                onClick={() => setActiveTab('presets')}
                className={cn(
                  'flex-1 px-4 py-2 text-sm font-medium',
                  'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-mv-blue-500',
                  activeTab === 'presets'
                    ? 'border-b-2 border-mv-blue-600 text-mv-blue-600'
                    : 'text-mv-gray-600 hover:text-mv-gray-900'
                )}
              >
                {locale === 'nl-NL' ? 'Voorinstellingen' : 'Presets'}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('custom')}
                className={cn(
                  'flex-1 px-4 py-2 text-sm font-medium',
                  'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-mv-blue-500',
                  activeTab === 'custom'
                    ? 'border-b-2 border-mv-blue-600 text-mv-blue-600'
                    : 'text-mv-gray-600 hover:text-mv-gray-900'
                )}
              >
                {locale === 'nl-NL' ? 'Aangepast' : 'Custom'}
              </button>
            </div>

            {/* Content */}
            <div className="p-2">
              {activeTab === 'presets' ? (
                <div className="space-y-1">
                  {availablePresets.map((preset) => (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => handlePresetClick(preset)}
                      className={cn(
                        'w-full rounded px-3 py-2 text-left text-sm',
                        'hover:bg-mv-gray-100',
                        'focus:outline-none focus:ring-2 focus:ring-mv-blue-500'
                      )}
                    >
                      {locale === 'nl-NL' ? preset.labelNl : preset.label}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="space-y-3 p-2">
                  {/* Start date */}
                  <div>
                    <label className="mb-1 block text-xs font-medium text-mv-gray-700">
                      {locale === 'nl-NL' ? 'Startdatum' : 'Start date'}
                    </label>
                    <input
                      type="date"
                      value={customStart}
                      onChange={(e) => setCustomStart(e.target.value)}
                      min={minDate ? formatDateForInput(minDate) : undefined}
                      max={maxDate ? formatDateForInput(maxDate) : undefined}
                      className={cn(
                        'flex h-9 w-full rounded border border-mv-gray-300 bg-white px-3 py-2 text-sm',
                        'focus:border-mv-blue-500 focus:outline-none focus:ring-2 focus:ring-mv-blue-500/20'
                      )}
                    />
                  </div>

                  {/* End date */}
                  <div>
                    <label className="mb-1 block text-xs font-medium text-mv-gray-700">
                      {locale === 'nl-NL' ? 'Einddatum' : 'End date'}
                    </label>
                    <input
                      type="date"
                      value={customEnd}
                      onChange={(e) => setCustomEnd(e.target.value)}
                      min={customStart || (minDate ? formatDateForInput(minDate) : undefined)}
                      max={maxDate ? formatDateForInput(maxDate) : undefined}
                      className={cn(
                        'flex h-9 w-full rounded border border-mv-gray-300 bg-white px-3 py-2 text-sm',
                        'focus:border-mv-blue-500 focus:outline-none focus:ring-2 focus:ring-mv-blue-500/20'
                      )}
                    />
                  </div>

                  {/* Apply button */}
                  <button
                    type="button"
                    onClick={handleCustomApply}
                    disabled={!customStart || !customEnd}
                    className={cn(
                      'w-full rounded bg-mv-blue-600 px-4 py-2 text-sm font-medium text-white',
                      'hover:bg-mv-blue-900',
                      'focus:outline-none focus:ring-2 focus:ring-mv-blue-500 focus:ring-offset-2',
                      'disabled:cursor-not-allowed disabled:opacity-50'
                    )}
                  >
                    {locale === 'nl-NL' ? 'Toepassen' : 'Apply'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

DateRangePicker.displayName = 'DateRangePicker';

// Re-export types and utilities
export { DATE_RANGE_PRESETS, formatDateRange, formatDateForInput, parseDateFromInput };
export type { DateRange, DateRangePreset };
